import type React from 'react';
import { Fragment, useRef } from 'react';

import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import {
  Navigate,
  type PathRouteProps,
  Route,
  Routes,
  useSearchParams,
} from 'react-router-dom';

import { RaLink } from '@modules/_shared/components/RaLink';
import { getKeys } from '@modules/_shared/utils/objects';
import {
  ListingMarketingDrawer,
  type ListingMarketingDrawerProps,
} from '@modules/listings/components/ListingMarketingDrawer';

export type InDrawerSidebarItem<ItemPath extends string> = {
  label: React.ReactNode;
  secondary?: React.ReactNode;
  path: ItemPath;
};

export type InDrawerSidebarRoutesDefinitions<ItemPath extends string> = Record<
  ItemPath,
  Omit<PathRouteProps, 'path'> & {
    DrawerProps?: Omit<ListingMarketingDrawerProps, 'container'>;
  }
>;

type InDrawerSidebarProps<ItemPath extends string> = {
  items: InDrawerSidebarItem<ItemPath>[];
  selectedItem: string | undefined;
  routesDefinitions: InDrawerSidebarRoutesDefinitions<ItemPath>;
  redirectOnIndex?: ItemPath;
};

export const InDrawerSidebar = <ItemPath extends string>({
  items,
  selectedItem,
  routesDefinitions,
  redirectOnIndex,
}: InDrawerSidebarProps<ItemPath>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        minWidth: 0,
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <Box
        sx={{
          bgcolor: 'background.paper',
          height: { md: '100%' },
          width: { md: 300 },
          minWidth: { md: 300 },
          flexShrink: 0,
          borderRight: { md: 1 },
          borderColor: { md: 'divider' },
        }}
      >
        <List
          sx={{
            width: '100%',
            bgcolor: 'background.paper',
            paddingTop: 0,
          }}
        >
          {items.map(item => (
            <Fragment key={item.path}>
              <ListItem alignItems="flex-start" disablePadding>
                <ListItemButton
                  component={RaLink}
                  to={item.path}
                  selected={selectedItem === item.path}
                  preserveSearchParams
                >
                  <ListItemText
                    primary={item.label}
                    secondary={item.secondary}
                  />
                </ListItemButton>
              </ListItem>
              <Divider variant="fullWidth" component="li" />
            </Fragment>
          ))}
        </List>
      </Box>
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Routes>
          {redirectOnIndex != null && (
            <Route
              index={!isMobile}
              element={
                <Navigate
                  to={{
                    pathname: redirectOnIndex,
                    search: searchParams.toString(),
                  }}
                />
              }
            />
          )}
          {getKeys(routesDefinitions).map(path => {
            const { element, DrawerProps, ...definition } =
              routesDefinitions[path];

            return (
              <Route
                key={path}
                path={`${path}/*`}
                {...definition}
                element={
                  element && (
                    <ListingMarketingDrawer
                      container={containerRef.current}
                      title={DrawerProps?.title ?? ''}
                      slotProps={DrawerProps?.slotProps}
                      width={DrawerProps?.width}
                    >
                      {element}
                    </ListingMarketingDrawer>
                  )
                }
              />
            );
          })}
        </Routes>
      </Box>
    </Box>
  );
};
